import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Activity, AlertTriangle, BookOpen, StickyNote, Contact, CalendarClock, ListTodo, CreditCard, Menu, X, Eraser, FileText, Globe, Inbox, Search, SlidersHorizontal, LayoutDashboard, MessageSquare, Sparkles, Star, Users } from 'lucide-react';
import ReplaiMark from '../components/ReplaiMark';
import { useAuth } from '../contexts/AuthProvider';
import axiosInstance from '../utils/axiosInstance';
import './v2.css';

// Grouped by how the team uses it (Geoff, 2026-10-19: "a lot of items, they all
// look the same"): each group has its own accent. `only: 'lead'` = the support
// leads, checked on the server too. `count` = a badge from /nav-counts/.
const NAV = [
  { group: 'Daily work', accent: '#b8892a', badge: '#8a6416', items: [
    { to: '/tickets', label: 'Tickets', icon: Inbox },
    { to: '/scheduled', label: 'Scheduled replies', icon: CalendarClock, count: 'scheduled' },
    { to: '/flow', label: 'Live flow', icon: Activity },
    { to: '/actions', label: 'Actions', icon: ListTodo, count: 'actions' },
    { to: '/orders', label: 'Late orders', icon: AlertTriangle },
    { to: '/customers', label: 'Customers', icon: Contact },
    { to: '/cases', label: 'Cases', icon: CreditCard },
    { to: '/reviews', label: 'Reviews', icon: Star },
  ] },
  { group: 'Replai', accent: '#3d5fc4', items: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/feedback', label: 'Feedback', icon: MessageSquare, count: 'feedback' },
    { to: '/playground', label: 'Playground', icon: Sparkles },
    { to: '/bible', label: 'Bible', icon: BookOpen },
    { to: '/notes', label: 'Team notes', icon: StickyNote },
  ] },
  { group: 'Look up', accent: '#4f7a63', items: [
    { to: '/orders/lookup', label: 'Order lookup', icon: Search },
    { to: '/orders/guide', label: 'Order guide', icon: FileText },
    { to: '/customs', label: 'HS codes', icon: Globe },
  ] },
  { group: 'Leads', accent: '#8a939e', items: [
    { to: '/control', label: 'Control', icon: SlidersHorizontal, only: 'lead' },
    { to: '/orders/cleanup', label: 'Order cleanup', icon: Eraser, only: 'lead' },
    { to: '/insights', label: 'Agent insights', icon: Users, only: 'lead' },
  ] },
];

// Pages only some people see (Agent insights: the leads). Asked once per load.
let accessCache = null;
let countsCache = null;

const V2Shell = ({ children }) => {
  const { user } = useAuth();
  const [access, setAccess] = useState(accessCache || {});
  const [open, setOpen] = useState(false); // the menu on small screens
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (accessCache) return;
    axiosInstance.get('api/replai/v2/access/').then(({ data }) => { accessCache = data; setAccess(data); }).catch(() => {});
  }, []);
  const [counts, setCounts] = useState(countsCache || {});
  useEffect(() => {
    const load = () => axiosInstance.get('api/replai/v2/nav-counts/')
      .then(({ data }) => { countsCache = data; setCounts(data); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    document.body.classList.add('v2-active');
    return () => document.body.classList.remove('v2-active');
  }, []);

  return (
    <div className="v2">
      <div className="border-r border-[var(--line)] bg-[var(--surface)]">
      <aside className="v2-rail sticky top-0 flex h-screen flex-col px-3 py-5">
        <div className="flex items-center justify-between gap-2.5 px-2">
          <div className="flex items-center gap-2.5">
            <ReplaiMark size={26} />
            <span className="text-[1.05rem] font-semibold">Replai</span>
          </div>
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? 'Close menu' : 'Open menu'}
            className="v2-menu-btn rounded-md p-1.5 text-[var(--ink-2)] hover:bg-[var(--paper)]">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        <nav className={`v2-nav mt-5 min-h-0 flex-1 space-y-4 overflow-y-auto ${open ? 'is-open' : ''}`}>
          {NAV.map(({ group, accent, badge, items }) => {
            const shown = items.filter((it) => !it.only || access[it.only]);
            if (!shown.length) return null;
            return (
              <div key={group} className="rounded-lg">
                <div className="flex items-center gap-1.5 px-2 pb-1 text-[0.68rem] font-semibold uppercase tracking-wider text-[var(--ink-3)]">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />{group}
                </div>
                <div className="space-y-px border-l-2 pl-1" style={{ borderColor: `${accent}33` }}>
                  {shown.map(({ to, label, icon: Icon, count }) => {
                    const n = count ? counts[count] : 0;
                    return (
                      <NavLink
                        key={to}
                        to={to}
                        end={to === '/orders'}
                        className={({ isActive }) => `flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-[0.9rem] ${
                          isActive ? 'bg-[var(--paper)] font-semibold text-[var(--ink)]' : 'text-[var(--ink-2)] hover:bg-[var(--paper)]'
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} style={{ color: accent }} />
                        <span className="flex-1">{label}</span>
                        {n > 0 && (
                          <span className="num rounded-full px-1.5 text-[0.7rem] font-semibold leading-5 text-white" style={{ background: badge || accent }}>
                            {n > 99 ? '99+' : n}</span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="v2-user mt-3 truncate px-2 text-sm text-[var(--ink-3)]">{user?.displayName}</div>
      </aside>
      </div>
      <main className="min-w-0">{children}</main>
    </div>
  );
};

export default V2Shell;
